<template>
  <FileInput
    id="file-upload"
    ref="fileInput"
    hidden
    :accept="imageTypes"
    :multiple="false"
    :limit="1"
    @input="onInput"
  />
  <div class="border-b border-secondary">
    <div class="px-3 py-1.5 flex items-center justify-between">
      <button
        class="flex items-center gap-2"
        :disabled="!(buildings.length > 1)"
        @click="emit('goToStep', 'buildings')"
      >
        <div
          class="flex items-center py-1 px-1.5 gap-2 bg-secondary border border-tertiary rounded-sm text-primary data-[placeholder]:text-placeholder text-sm leading-none font-medium"
        >
          <Indicator
            variant="building"
            :mode="building ? 'solid' : 'transparent'"
            size="3xs"
            :imageUrl="building?.imageUrl"
          />
          <span>{{ buildingName }}</span>
        </div>
      </button>
      <CloseButton @click="emit('update:open', false)" size="sm" />
    </div>
    <div class="px-4 pt-2 pb-4">
      <div class="flex gap-5 items-center">
        <div
          class="position-relative cursor-pointer"
          @click="fileInput.click()"
        >
          <div
            class="border border-secondary rounded-md position-absolute p-2 right-0 bg-secondary"
          >
            <Icon name="camera" size="md" />
          </div>
          <Indicator
            variant="asset"
            size="4xl"
            class="border border-secondary rounded-4xl mt-2 mr-2"
            :imageUrl="imageUrl"
            :class="imageUrl && 'bg-primary'"
          />
        </div>
        <div class="flex flex-col gap-2 justify-start truncate">
          <EditableTextArea
            :modelValue="form.name"
            @update:modelValue="handleUpdateForm('name', $event)"
            submitMode="both"
            class="text-2xl font-medium leading-8"
            :placeholder="t('components.asset_create.name.placeholder')"
            :class="
              isEmpty(form.name) ? 'text-placeholder_subtle' : 'text-primary'
            "
          />
          <div class="flex flex-col justify-start">
            <template v-if="!isIdentifierVisible">
              <Component
                :is="
                  !isEmpty(form.identifier)
                    ? IdentifierDisplay
                    : AddIdentifierBadge
                "
                :identifier="form.identifier"
                @click="handleShowIdentifier(true)"
              />
            </template>
            <template v-if="isIdentifierVisible">
              <template v-if="!maxSmall">
                <IdentifierInput
                  :modelValue="form.identifier"
                  @update:modelValue="handleUpdateForm('identifier', $event)"
                  @submit="handleShowIdentifier(false)"
                />
              </template>
              <template v-else>
                <TextPropertyDrawer
                  :title="t('components.asset_create.unique_id.placeholder')"
                  :initialState="form.identifier"
                  @submit="handleUpdateForm('identifier', $event)"
                  @update:open="handleShowIdentifier($event)"
                  :open="isIdentifierVisible"
                />
                <Component
                  :is="
                    !isEmpty(form.identifier)
                      ? IdentifierDisplay
                      : AddIdentifierBadge
                  "
                  :identifier="form.identifier"
                  @click="handleShowIdentifier(true)"
                />
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { isEmpty } from "lodash";
import { Indicator } from "@/SharedComponents/Indicator";
import { CloseButton } from "@/SharedComponents/Buttons";
import { EditableTextArea, FileInput } from "@/SharedComponents/Inputs";
import {
  TextPropertyDrawer,
  IdentifierDisplay,
  IdentifierInput,
  AddIdentifierBadge,
} from "@/Pages/Assets/Partials/Create";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { Icon } from "@/SharedComponents/Icon";
import { imageTypes } from "@/SharedComponents/Inputs";

// Breakpoints
const { maxSmall } = useBreakpoints();

// Translations
const { t } = useI18n();

// Refs
const isIdentifierVisible = ref(false);

// Define Emits
const emit = defineEmits(["update:open", "goToStep", "update:form"]);

// Define Props
const props = defineProps({
  buildings: { type: Array, required: true },
  buildingId: { type: String, required: true },
  form: { type: Object, required: true },
});

const fileInput = ref();
const imageUrl = ref(null);

const onInput = (files) => {
  let filteredFiles = fileInput.value.filterValidFiles(files);

  if (filteredFiles.length > 0) {
    const file = filteredFiles[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      imageUrl.value = e.target.result;
    };
    reader.readAsDataURL(file);
    handleUpdateForm("photo", file);
  }
};

// Computed Props
const building = computed(() =>
  isEmpty(props.buildings) && isEmpty(props.buildingId)
    ? null
    : props.buildings.find((building) => building.id === props.buildingId)
);

const buildingName = computed(
  () => building.value?.name || t("components.select.building.placeholder")
);

// Functions
const handleUpdateForm = (property, value) => {
  emit("update:form", { property, value });
  if (maxSmall.value) isIdentifierVisible.value = false;
};

const handleShowIdentifier = (value) => {
  isIdentifierVisible.value = value;
};
</script>
